import { renderFields } from "@highnoteplatform/card-viewer";
import { toast } from "react-toastify";

type FieldStyles = Parameters<
  typeof renderFields
>[0]["elements"]["cardNumber"]["styles"];

export interface HighnoteCardSelectors {
  cardNumber: string;
  cvv: string;
  expirationDate: string;
}

// Chrome can keep showing a Highnote frame's blank first paint until the frame's size changes.
const repaintFrames = (selectors: HighnoteCardSelectors) => {
  const frames = Object.values(selectors).flatMap((selector) =>
    Array.from(
      document.querySelectorAll<HTMLIFrameElement>(`${selector} > iframe`),
    ),
  );
  frames.forEach((frame) => {
    frame.style.width = `${frame.offsetWidth + 1}px`;
  });
  requestAnimationFrame(() =>
    requestAnimationFrame(() =>
      frames.forEach((frame) => frame.style.removeProperty("width")),
    ),
  );
};

/** Resolves once the fields have loaded and rejects when the SDK reports a failure first. */
export const renderHighnoteCardFields = ({
  clientToken,
  paymentCardId,
  selectors,
  styles,
  t,
}: {
  clientToken: string;
  paymentCardId: string;
  selectors: HighnoteCardSelectors;
  styles: FieldStyles;
  t: (key: string) => string;
}) =>
  new Promise<void>((resolve, reject) => {
    let settled = false;
    let ready = false;
    let toggleMask: (() => void) | undefined;
    // `onReady` and the render promise can arrive in either order; unmask once both have.
    const unmask = () => {
      if (!ready || !toggleMask) return;
      toggleMask();
      repaintFrames(selectors);
    };

    renderFields({
      clientToken,
      paymentCardId,
      enableClipboard: true,
      ...(process.env.REACT_APP_SENTRY_ENV === "production" && {
        environment: "live" as const,
      }),
      onCopyToClipboardSuccess: ({ field }) => {
        const label =
          {
            cardNumber: t("Card number"),
            cvv: t("CVV"),
            expirationDate: t("Expiration date"),
          }[field] ?? String(field);
        toast.success(`${label} ${t("copied to clipboard")}`);
      },
      onError: (error) => {
        console.error(error);
        if (settled) {
          toast.error(t("SomethingWentWrongDescriptionWithSupport"));
          return;
        }
        settled = true;
        reject(error);
      },
      onReady: () => {
        ready = true;
        unmask();
        if (!settled) {
          settled = true;
          resolve();
        }
      },
      elements: {
        cardNumber: { selector: selectors.cardNumber, styles },
        cvv: { selector: selectors.cvv, styles },
        expirationDate: { selector: selectors.expirationDate, styles },
      },
    })
      .then(({ toggleCardNumberMask }) => {
        toggleMask = toggleCardNumberMask;
        unmask();
      })
      .catch((error) => {
        if (settled) return;
        settled = true;
        reject(error);
      });
  });
